import React, { useMemo, useState } from 'react';
import { Megaphone, Send, Trash2, Users, UserCheck, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { PageHeader, Card, Avatar } from './shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const timeAgo = (iso) => {
  if (!iso) return '';
  const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'Ahora'; if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `Hace ${h} h`;
  return `Hace ${Math.floor(h / 24)} d`;
};

const QUICK = [
  '🌧️ Por la lluvia, bono de L 10 extra por cada pedido entregado hoy.',
  'Recuerda usar el chaleco reflectivo y casco en todas tus entregas.',
  'Alta demanda esta noche: mantente disponible para más pedidos.',
];

const AdminDriverNotifications = () => {
  const { drivers, driverNotifications, createDriverNotification, deleteDriverNotification } = useApp();
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [scope, setScope] = useState('all'); // all | selected
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const deliveryDrivers = useMemo(() => drivers.filter((d) => d.approval === 'approved'), [drivers]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const targetCount = scope === 'all' ? deliveryDrivers.length : selected.length;

  const send = async () => {
    if (!text.trim()) return toast.error('Escribe el contenido del aviso');
    if (scope === 'selected' && selected.length === 0) return toast.error('Selecciona al menos un conductor');
    setBusy(true);
    try {
      const camp = await createDriverNotification({ title: title.trim() || null, text: text.trim(), driverIds: scope === 'selected' ? selected : null });
      toast.success(`Aviso enviado a ${camp.count} conductor${camp.count === 1 ? '' : 'es'}`);
      setTitle(''); setText(''); setSelected([]); setScope('all');
    } catch { /* toast shown by context */ } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Avisos a conductores" subtitle="Crea y envía notificaciones (bonos, instrucciones o anuncios) a los repartidores" />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Composer */}
        <Card className="lg:col-span-3">
          <div className="flex items-center gap-2 mb-4"><div className="w-10 h-10 rounded-xl bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center"><Megaphone size={20} /></div><div><h3 className="font-extrabold text-[#1E2130]">Nuevo aviso</h3><p className="text-xs text-gray-500">Llega a la app del conductor al instante</p></div></div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Título (opcional)</Label>
              <Input data-testid="dn-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Bono por lluvia" maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Mensaje</Label>
              <Textarea data-testid="dn-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={500} placeholder="Ej. Por la lluvia de hoy, ganas L 10 extra por cada pedido entregado." />
              <div className="flex flex-wrap gap-1.5">
                {QUICK.map((q, i) => <button key={i} type="button" data-testid={`dn-quick-${i}`} onClick={() => setText(q)} className="text-[11px] px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">{q.length > 42 ? `${q.slice(0, 40)}…` : q}</button>)}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-gray-600">Destinatarios</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="dn-scope-all" onClick={() => setScope('all')} className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${scope === 'all' ? 'border-[#EE3B36] bg-[#FFF7F7]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <Users size={18} className={scope === 'all' ? 'text-[#EE3B36]' : 'text-gray-400'} />
                  <div><p className="text-sm font-semibold">Todos</p><p className="text-[11px] text-gray-500">{deliveryDrivers.length} conductores</p></div>
                </button>
                <button type="button" data-testid="dn-scope-selected" onClick={() => setScope('selected')} className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${scope === 'selected' ? 'border-[#EE3B36] bg-[#FFF7F7]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <UserCheck size={18} className={scope === 'selected' ? 'text-[#EE3B36]' : 'text-gray-400'} />
                  <div><p className="text-sm font-semibold">Seleccionar</p><p className="text-[11px] text-gray-500">{selected.length} elegidos</p></div>
                </button>
              </div>
            </div>

            {scope === 'selected' && (
              <div className="border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-64 overflow-y-auto" data-testid="dn-driver-list">
                {deliveryDrivers.length === 0 && <p className="text-center text-sm text-gray-400 py-6">No hay conductores aprobados</p>}
                {deliveryDrivers.map((d) => (
                  <label key={d.id} data-testid={`dn-driver-${d.id}`} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} className="w-4 h-4 accent-[#EE3B36]" />
                    <Avatar src={d.avatar} name={d.name} size={32} />
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{d.name}</p><p className="text-[11px] text-gray-500">{d.phone}</p></div>
                    {d.gender === 'mujer' && <span className="text-[10px] font-semibold text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">Conductora</span>}
                  </label>
                ))}
              </div>
            )}

            <Button data-testid="dn-send" onClick={send} disabled={busy} className="w-full bg-[#EE3B36] hover:bg-[#d43530] gap-2 h-11">
              <Send size={16} /> {busy ? 'Enviando...' : `Enviar aviso${targetCount ? ` (${targetCount})` : ''}`}
            </Button>
          </div>
        </Card>

        {/* History */}
        <Card className="lg:col-span-2" title="Avisos enviados">
          <ul className="divide-y divide-gray-100 -mx-1" data-testid="dn-history">
            {(!driverNotifications || driverNotifications.length === 0) && <li className="text-center text-gray-400 py-12 text-sm">Aún no has enviado avisos</li>}
            {(driverNotifications || []).map((n) => (
              <li key={n.id} data-testid={`dn-item-${n.id}`} className="flex items-start gap-3 px-1 py-3.5">
                <div className="w-9 h-9 rounded-xl bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center shrink-0"><Bell size={17} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#1E2130] truncate">{n.title}</p>
                  <p className="text-sm text-gray-600 line-clamp-2">{n.text}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.createdAt)} • {n.scope === 'all' ? 'Todos' : 'Seleccionados'} • {n.count} conductor{n.count === 1 ? '' : 'es'}</p>
                </div>
                <button data-testid={`dn-delete-${n.id}`} onClick={() => setConfirmDel(n)} className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar aviso del historial</AlertDialogTitle>
            <AlertDialogDescription>Esto solo lo quita del historial del panel. Los conductores que ya lo recibieron lo seguirán viendo en su app.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="dn-delete-confirm" className="bg-red-600 hover:bg-red-700" onClick={() => { deleteDriverNotification(confirmDel.id).then(() => toast('Aviso eliminado del historial')); setConfirmDel(null); }}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDriverNotifications;
